import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getDownloadURL, uploadBytesResumable, ref } from "firebase/storage";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db, storage } from "../firebase.config";
import { toast } from "react-toastify";
import { useAuth } from "../contexts/AuthContext";
import {
  FiUpload,
  FiAnchor,
  FiPhone,
  FiMapPin,
  FiAward,
  FiImage,
  FiCheck,
  FiPackage,
  FiMail,
  FiGlobe,
  FiClock,
  FiArrowLeft,
  FiLock,
  FiUser,
} from "react-icons/fi";
import { MdStorefront, MdVerified } from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";

const RegisterShowroom = () => {
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  const [thumbnail, setThumbnail] = useState("");
  const [progress, setProgress] = useState(0);
  const [sellerName, setSellerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [description, setDescription] = useState("");
  const [specialization, setSpecialization] = useState("");
  const [yearsOfExperience, setYearsOfExperience] = useState("");
  const [website, setWebsite] = useState("");
  const [workingHours, setWorkingHours] = useState("");
  const [uploading, setUploading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Check authentication on component mount
  useEffect(() => {
    // Wait for auth to finish loading
    if (!authLoading) {
      setIsLoading(false);
      if (!currentUser) {
        toast.info("Please login to register your showroom");
        navigate("/sign-in", { state: { from: "/register-showroom" } });
      }
    }
  }, [currentUser, authLoading, navigate]);

  // Handle form submission for file upload
  const handleImageUpload = (e) => {
    e.preventDefault();
    const file = e.target[0].files[0];
    uploadFile(file);
  };

  // File upload function
  const uploadFile = (file) => {
    if (!file) {
      toast.error("Please choose a file.");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB.");
      return;
    }

    setUploading(true);
    const uniqueName = `${Date.now()}-${file.name}`;
    const storageRef = ref(storage, `showroom-thumbnails/${uniqueName}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const prog = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
        );
        setProgress(prog);
      },
      (error) => {
        console.error("Upload error:", error);
        toast.error("Failed to upload image. Please try again.");
        setUploading(false);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((url) => {
          setThumbnail(url);
          toast.success("Showroom logo uploaded successfully!");
          setUploading(false);
        });
      },
    );
  };

  // Handle input changes
  const handlePhoneNumberChange = (e) => {
    const rawValue = e.target.value.replace(/[^\d]/g, "");
    if (rawValue.length > 10) return;
    setPhoneNumber(
      rawValue.replace(/(\d{3})(\d{3})(\d{4})/, "+92-$1-$2-$3").slice(0, 16),
    );
  };

  // Save showroom to Firestore
  const saveShowroom = async () => {
    if (!currentUser) {
      toast.error("Please login to register your showroom");
      navigate("/sign-in", { state: { from: "/register-showroom" } });
      return;
    }

    if (
      !sellerName ||
      !phoneNumber ||
      !email ||
      !address ||
      !businessName ||
      !thumbnail
    ) {
      toast.error("Please fill in all required fields and upload a logo.");
      return;
    }

    if (!termsAccepted) {
      toast.error("Please accept the terms and conditions.");
      return;
    }

    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    const showroomData = {
      thumbnail,
      sellerName,
      phoneNumber,
      email,
      address,
      businessName,
      description: description || "Showroom specializing in quality vehicles.",
      specialization: specialization || "General Automotive",
      yearsOfExperience: yearsOfExperience || "0",
      website: website || "",
      workingHours: workingHours || "Mon-Sat: 9:00 AM - 6:00 PM",
      createdAt: serverTimestamp(),
      status: "pending", // pending approval
      verified: false,
      totalListings: 0,
      rating: 0,
      isShowroom: true,
      registeredAt: serverTimestamp(),
      userId: currentUser.uid, // Associate with the authenticated user
      userEmail: currentUser.email,
    };

    try {
      await addDoc(collection(db, "Feature-Seller"), showroomData);
      toast.success(
        "🎉 Showroom registered successfully! We'll review your application shortly.",
      );

      // Reset form
      setThumbnail("");
      setSellerName("");
      setPhoneNumber("");
      setEmail("");
      setAddress("");
      setBusinessName("");
      setDescription("");
      setSpecialization("");
      setYearsOfExperience("");
      setWebsite("");
      setWorkingHours("");
      setTermsAccepted(false);
      setProgress(0);

      // Reset file input
      document.getElementById("imageUpload").value = "";

      // Redirect to home after success
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error) {
      console.error("Error adding document: ", error);
      toast.error("Failed to register showroom. Please try again.");
    }
  };

  const specializations = [
    "Luxury Vehicles",
    "Sports Cars",
    "SUVs & 4x4s",
    "Electric Vehicles",
    "Vintage Cars",
    "Commercial Vehicles",
    "Motorcycles",
    "Auto Parts",
    "Car Services",
    "General Automotive",
  ];

  // Show loading state while checking authentication
  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // If not logged in, show the authentication required screen
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiLock className="text-4xl text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Authentication Required
          </h2>
          <p className="text-gray-500 mb-8">
            Please login to your account to register your showroom.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() =>
                navigate("/sign-in", { state: { from: "/register-showroom" } })
              }
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
            >
              Login Now
            </button>
            <button
              onClick={() => navigate("/")}
              className="w-full py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl shadow-xl mb-6 transform rotate-3 hover:rotate-0 transition-transform duration-300">
              <MdStorefront className="text-4xl text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 bg-clip-text text-transparent">
              Register Your Showroom
            </h1>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto text-lg">
              Join NCP Wheels as a trusted showroom partner and reach thousands
              of car buyers
            </p>
            <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
              <span className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
                <MdVerified className="text-green-600" />
                Trusted Platform
              </span>
              <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                <FiUser className="text-blue-600" />
                Logged In
              </span>
              <span className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">
                <FiAnchor className="text-purple-600" />
                1000+ Active Buyers
              </span>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  1
                </div>
                <span className="text-sm font-medium text-gray-700 hidden sm:inline">
                  Details
                </span>
              </div>
              <div className="w-20 h-0.5 bg-blue-600"></div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm font-bold">
                  2
                </div>
                <span className="text-sm font-medium text-gray-500 hidden sm:inline">
                  Upload
                </span>
              </div>
              <div className="w-20 h-0.5 bg-gray-300"></div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-sm font-bold">
                  3
                </div>
                <span className="text-sm font-medium text-gray-500 hidden sm:inline">
                  Review
                </span>
              </div>
            </div>
          </div>

          {/* Main Form */}
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-200/50 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 md:p-8">
              {/* Left Column - Image Upload & Contact Info */}
              <div className="space-y-6">
                {/* Image Upload Section */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                  <div className="flex items-center mb-4">
                    <FiImage className="text-xl text-blue-600 mr-3" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Showroom Logo
                    </h2>
                    <span className="text-red-500 ml-1">*</span>
                  </div>

                  <form onSubmit={handleImageUpload} className="space-y-4">
                    <div className="relative">
                      <input
                        type="file"
                        id="imageUpload"
                        name="image"
                        accept="image/*"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            uploadFile(e.target.files[0]);
                          }
                        }}
                      />
                      <div className="border-2 border-dashed border-gray-300/50 rounded-xl p-8 text-center hover:border-blue-400 transition-colors duration-200 bg-white/50">
                        {thumbnail ? (
                          <div className="space-y-3">
                            <img
                              src={thumbnail}
                              alt="Logo Preview"
                              className="w-32 h-32 object-cover rounded-xl mx-auto border-2 border-green-200 shadow-lg"
                            />
                            <div className="flex items-center justify-center text-green-600">
                              <FiCheck className="mr-2" />
                              <span>Logo uploaded!</span>
                            </div>
                            <p className="text-xs text-gray-500">
                              Click to change logo
                            </p>
                          </div>
                        ) : (
                          <>
                            <FiUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600 mb-2">
                              <span className="font-semibold text-blue-600">
                                Click to upload
                              </span>{" "}
                              or drag and drop
                            </p>
                            <p className="text-gray-400 text-sm">
                              PNG, JPG up to 5MB
                            </p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    {progress > 0 && progress < 100 && (
                      <div className="mt-4 space-y-2">
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Uploading...</span>
                          <span>{progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </form>
                </div>

                {/* Contact Information */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-100">
                  <div className="flex items-center mb-4">
                    <FiPhone className="text-xl text-emerald-600 mr-3" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Contact Information
                    </h2>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="phoneNumber"
                        className="flex items-center text-sm font-medium text-gray-700 mb-2"
                      >
                        <FiPhone className="mr-2" />
                        Phone Number
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center">
                          <span className="text-xl mr-2">🇵🇰</span>
                          <span className="text-gray-500">+92</span>
                        </div>
                        <input
                          type="text"
                          id="phoneNumber"
                          value={phoneNumber}
                          onChange={handlePhoneNumberChange}
                          maxLength={16}
                          className="w-full pl-20 pr-4 py-3 bg-white border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all duration-200"
                          placeholder="XXX-XXX-XXXX"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="flex items-center text-sm font-medium text-gray-700 mb-2"
                      >
                        <FiMail className="mr-2" />
                        Email Address
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-4 pr-4 py-3 bg-white border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all duration-200"
                        placeholder="showroom@example.com"
                        required
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="address"
                        className="flex items-center text-sm font-medium text-gray-700 mb-2"
                      >
                        <FiMapPin className="mr-2" />
                        Business Address
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        id="address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="w-full pl-4 pr-4 py-3 bg-white border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all duration-200"
                        placeholder="Enter complete business address"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* WhatsApp Contact */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                  <div className="flex items-center gap-3 text-green-700">
                    <FaWhatsapp className="text-2xl" />
                    <div>
                      <p className="font-medium">Connect via WhatsApp</p>
                      <p className="text-sm text-gray-600">
                        We'll reach out to you for verification
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Showroom Details */}
              <div className="space-y-6">
                {/* Showroom Information */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
                  <div className="flex items-center mb-4">
                    <MdStorefront className="text-xl text-purple-600 mr-3" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Showroom Details
                    </h2>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="sellerName"
                        className="flex items-center text-sm font-medium text-gray-700 mb-2"
                      >
                        <FiUser className="mr-2" />
                        Business Name
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        id="sellerName"
                        value={sellerName}
                        onChange={(e) => setSellerName(e.target.value)}
                        className="w-full pl-4 pr-4 py-3 bg-white border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-all duration-200"
                        placeholder="Enter Showroom's full name"
                        required
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="businessName"
                        className="flex items-center text-sm font-medium text-gray-700 mb-2"
                      >
                        <MdStorefront className="mr-2" />
                        Owner's Name
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        id="businessName"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        className="w-full pl-4 pr-4 py-3 bg-white border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-all duration-200"
                        placeholder="Enter owner's name"
                        required
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="specialization"
                        className="flex items-center text-sm font-medium text-gray-700 mb-2"
                      >
                        <FiPackage className="mr-2" />
                        Specialization
                      </label>
                      <select
                        id="specialization"
                        value={specialization}
                        onChange={(e) => setSpecialization(e.target.value)}
                        className="w-full pl-4 pr-4 py-3 bg-white border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 transition-all duration-200"
                      >
                        <option value="">Select specialization</option>
                        {specializations.map((spec) => (
                          <option key={spec} value={spec}>
                            {spec}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Additional Details */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-100">
                  <div className="flex items-center mb-4">
                    <FiAward className="text-xl text-amber-600 mr-3" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Additional Information
                    </h2>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="yearsOfExperience"
                        className="flex items-center text-sm font-medium text-gray-700 mb-2"
                      >
                        <FiClock className="mr-2" />
                        Years in Business
                      </label>
                      <input
                        type="number"
                        id="yearsOfExperience"
                        value={yearsOfExperience}
                        onChange={(e) => setYearsOfExperience(e.target.value)}
                        min="0"
                        max="50"
                        className="w-full pl-4 pr-4 py-3 bg-white border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 transition-all duration-200"
                        placeholder="Enter years of experience"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="website"
                        className="flex items-center text-sm font-medium text-gray-700 mb-2"
                      >
                        <FiGlobe className="mr-2" />
                        Website (Optional)
                      </label>
                      <input
                        type="url"
                        id="website"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        className="w-full pl-4 pr-4 py-3 bg-white border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 transition-all duration-200"
                        placeholder="https://showroom.com"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="description"
                        className="flex items-center text-sm font-medium text-gray-700 mb-2"
                      >
                        <FiAnchor className="mr-2" />
                        Showroom Description
                      </label>
                      <textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows="3"
                        className="w-full pl-4 pr-4 py-3 bg-white border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 transition-all duration-200 resize-none"
                        placeholder="Tell buyers about your showroom, specialties, and services..."
                      />
                    </div>
                  </div>
                </div>

                {/* Preview Summary */}
                {(sellerName || businessName) && (
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Preview Summary
                    </h3>
                    <div className="space-y-3">
                      {businessName && (
                        <div className="flex items-center">
                          <MdStorefront className="text-purple-500 mr-3" />
                          <span className="text-gray-700">{businessName}</span>
                        </div>
                      )}
                      {sellerName && (
                        <div className="flex items-center">
                          <FiUser className="text-gray-400 mr-3" />
                          <span className="text-gray-700">{sellerName}</span>
                        </div>
                      )}
                      {thumbnail && (
                        <div className="flex items-center">
                          <FiImage className="text-gray-400 mr-3" />
                          <span className="text-gray-600">✓ Logo uploaded</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Terms & Submit */}
            <div className="px-6 md:px-8 py-6 bg-gray-50/50 border-t border-gray-200/50">
              <div className="max-w-4xl mx-auto">
                <div className="mb-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600">
                      I agree to the{" "}
                      <Link
                        to="/terms"
                        className="text-blue-600 hover:underline"
                      >
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link
                        to="/privacy-policy"
                        className="text-blue-600 hover:underline"
                      >
                        Privacy Policy
                      </Link>
                      . I confirm that all information provided is accurate and
                      I am authorized to register this showroom.
                      <span className="text-red-500 ml-1">*</span>
                    </span>
                  </label>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => navigate("/")}
                    className="px-8 py-3.5 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 font-semibold rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 hover:shadow-md w-full sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveShowroom}
                    disabled={uploading || !termsAccepted}
                    className="px-12 py-3.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25 w-full sm:w-auto transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center justify-center gap-3">
                      <MdStorefront className="text-xl" />
                      Register Showroom
                    </div>
                  </button>
                </div>

                <p className="text-center text-sm text-gray-400 mt-4">
                  By registering, you agree to receive communications from NCP
                  Wheels regarding your showroom application.
                </p>
              </div>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FiUser className="text-2xl text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Reach More Customers
              </h3>
              <p className="text-sm text-gray-500">
                Connect with thousands of active car buyers in Pakistan
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FiCheck className="text-2xl text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Easy Listing Management
              </h3>
              <p className="text-sm text-gray-500">
                Simple dashboard to manage your inventory and listings
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
              <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FiAward className="text-2xl text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Trusted & Verified
              </h3>
              <p className="text-sm text-gray-500">
                Build credibility with a verified showroom badge
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterShowroom;
